import React, { useContext, useRef, useState } from "react";
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Button,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Textarea,
    useMediaQuery,
} from "@chakra-ui/react";
import { Content, ContentAttachment } from "../../../common/types";
import { ExternalLink } from "../../../common/components/Links";
import { ContentActionsButtons } from "../../Contents/components/ContentActions";
import ContentContext from "../../../common/context/ContentContext";
import { debounce } from "../../../common/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronLeft,
    faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import TinyEditor from "../../Contents/components/TinyEditor";
import ContentAttachments from "./ContentAttachments";
import CloseButton from "./CloseButton";

export type DataInput = Pick<Content, "title" | "content" | "url">;
const DEBOUNCE_TIME = 60_000;

interface OwnProps {
    titleRef: React.MutableRefObject<string>;
    contentRef: React.MutableRefObject<string>;
    keyConceptsRef: React.MutableRefObject<string>;
    openQuestionsRef: React.MutableRefObject<string>;
    isAllSavedRef: React.MutableRefObject<boolean>;
    attachments: ContentAttachment[];
    onAttachmentUploaded: (a: ContentAttachment) => void;
    onLinkContent: () => void;
    patchContent: (payload?: object) => void;
    onSave: () => void;
    isSaving: boolean;
    onCloseWithAutosave?: () => void;
}

const ContentForm = ({
    titleRef,
    contentRef,
    keyConceptsRef,
    openQuestionsRef,
    isAllSavedRef,
    attachments,
    onAttachmentUploaded,
    onLinkContent,
    patchContent,
    isSaving,
    onSave,
    onCloseWithAutosave,
}: OwnProps) => {
    const content = useContext(ContentContext);
    const changesNotified = useRef(false);
    const [showMetaContent, setShowMetaContent] = useState(
        !!content?.keyConcepts || !!content?.openQuestions
    );
    const [isAtLeastMedium] = useMediaQuery("(min-width: 48em)");
    const [isLarge] = useMediaQuery("(min-width: 1200px)");

    const notifyChanges = () => {
        if (!changesNotified.current) {
            changesNotified.current = true;
            debounce(() => {
                patchContent({
                    title: titleRef.current,
                    content: contentRef.current,
                    keyConcepts: keyConceptsRef.current,
                    openQuestions: openQuestionsRef.current,
                });
                changesNotified.current = false;
            }, DEBOUNCE_TIME);
        }
    };

    const onChangeText = (
        { target }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ref: React.MutableRefObject<string>
    ) => {
        ref.current = target.value;
        isAllSavedRef.current = false;
        notifyChanges();
    };

    const MetaContentAccordion = () => (
        <Accordion allowToggle defaultIndex={0}>
            <AccordionItem border={0}>
                <AccordionButton>
                    <FormLabel fontSize="xl" flex={1}>
                        Conceptos clave {content?.keyConcepts && "*"}
                    </FormLabel>
                    <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                    <Textarea
                        placeholder="Escribe las ideas importantes, fechas, personajes, etc"
                        rows={15}
                        resize="vertical"
                        defaultValue={keyConceptsRef.current}
                        onChange={(e) => {
                            onChangeText(e, keyConceptsRef);
                        }}
                    />
                </AccordionPanel>
            </AccordionItem>
            <AccordionItem>
                <AccordionButton>
                    <FormLabel fontSize="xl" flex={1}>
                        Dudas {content?.openQuestions && "*"}
                    </FormLabel>
                    <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                    <Textarea
                        placeholder="¿Tengo que averiguar algo más? ¿Necesito ir más allá en algo?"
                        rows={15}
                        resize="vertical"
                        defaultValue={openQuestionsRef.current}
                        onChange={(e) => {
                            onChangeText(e, openQuestionsRef);
                        }}
                    />
                </AccordionPanel>
            </AccordionItem>
            <AccordionItem>
                <AccordionButton>
                    <FormLabel fontSize="xl" flex={1}>
                        Archivos {attachments.length > 0 && <span>({attachments.length})</span>}
                    </FormLabel>
                    <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                    <ContentAttachments contentId={content!._id} attachments={attachments} />
                </AccordionPanel>
            </AccordionItem>
        </Accordion>
    );

    const ToggleMetaContentButton = () => (
        <Button
            variant="ghost"
            leftIcon={
                <FontAwesomeIcon
                    icon={showMetaContent ? faChevronLeft : faChevronRight}
                />
            }
            onClick={() => {
                setShowMetaContent(showMetaContent ? false : true);
            }}
        >

        </Button>
    );

    return (
        <Box marginTop={1}>
            <FormControl isRequired marginTop={4} marginBottom={4}>
                <HStack>
                    <Input
                        variant="flushed"
                        fontSize={{ base: "2xl", md: "3xl" }}
                        type="text"
                        placeholder="Add a Title"
                        _placeholder={{
                            color: "black",
                            fontWeight: "bold",
                        }}
                        isRequired
                        defaultValue={content!.title}
                        onChange={(e) => {
                            onChangeText(e, titleRef);
                        }}
                    />
                    <CloseButton onCloseWithAutosave={onCloseWithAutosave} />
                </HStack>
            </FormControl>
            <FormControl>
                {content!.url && <ExternalLink url={content!.url} />}
            </FormControl>
            <FormControl>
                <ContentActionsButtons isSaving={isSaving} onSave={onSave} onLinkContent={onLinkContent} onAttachmentUploaded={onAttachmentUploaded} />
            </FormControl>
            <HStack h="100vh" w="100%" alignItems="flex-start">
                <Box
                    w={isAtLeastMedium ? (isLarge ? "30%" : "50%") : "96%"}
                    hidden={!showMetaContent}
                >
                    <MetaContentAccordion />
                </Box>
                <Box
                    w={isAtLeastMedium && showMetaContent ? (isLarge ? "70%" : "50%") : "96%"}
                    hidden={!isAtLeastMedium && showMetaContent}
                    h="100vh"
                >
                    <FormControl>
                        <HStack >
                            <ToggleMetaContentButton />
                            <FormLabel fontSize="2xl">Notas</FormLabel>
                        </HStack>
                        <TinyEditor defaultValue={content?.content ?? ""} onChange={(innerHTML: string) => {
                            contentRef.current = innerHTML;
                            notifyChanges();
                        }} />
                    </FormControl>
                </Box>
            </HStack>
        </Box>
    );
};

export default ContentForm;
